// Key name predates the multi-store world and is kept for back-compat:
// existing users' stamps must stay valid. It now governs ALL synced local
// caches (diagrams, patterns), not just diagrams. (spec 2026-07-15 §3)
const STORAGE_KEY = 'azabu-diagrams-owner';

/** Which action to take when reconciling the local diagram cache's owner
 *  stamp against the currently signed-in user. */
export type OwnershipAction = 'keep' | 'claim' | 'wipe-and-claim';

/** Pure decision, no I/O: `storedOwner` is whatever was last stamped into
 *  localStorage (`null` if nothing ever was). No stored owner means phase-1
 *  (pre-ownership) data — claimed by whichever user signs in first, which
 *  IS the desired one-time migration, not a bug. A stored owner matching
 *  `userId` is a no-op. A stored owner belonging to someone else means a
 *  shared-browser account switch: the cache must be wiped before the new
 *  user's diagrams can sync in, or the incoming user would see (and the
 *  background migration would re-upload) the previous user's diagrams. */
export function resolveOwnershipAction(storedOwner: string | null, userId: string): OwnershipAction {
	if (storedOwner == null) return 'claim';
	if (storedOwner === userId) return 'keep';
	return 'wipe-and-claim';
}

/** Reads the stamped owner of the local diagram cache. Returns `null` when
 *  no owner has ever been stamped (phase-1 data, or a fresh profile) and the
 *  sentinel `'unreadable'` when localStorage itself couldn't be read
 *  (private browsing, quota, disabled storage).
 *
 *  `'unreadable'` is deliberately a distinct type from `null`: collapsing
 *  "no owner recorded" and "storage is broken" into one value would tempt a
 *  caller into treating both as safe to wipe. Wiping on doubt can destroy
 *  diagrams that never got a chance to sync, so callers must treat
 *  `'unreadable'` as `'keep'` (do nothing) — see store-instance.ts's
 *  `prepare` wiring, which does exactly that. */
export function readCacheOwner(): string | null | 'unreadable' {
	try {
		return window.localStorage.getItem(STORAGE_KEY);
	} catch {
		return 'unreadable';
	}
}

/** Stamps the local diagram cache as owned by `userId`. Swallows storage
 *  failures with the same no-wipe-on-doubt posture as readCacheOwner: an
 *  unwritable stamp just means ownership gets re-evaluated from scratch
 *  next session, never that data is destroyed now. */
export function writeCacheOwner(userId: string): void {
	try {
		window.localStorage.setItem(STORAGE_KEY, userId);
	} catch {
		// Ignore storage failures (private mode, quota, etc.).
	}
}
